import React, { useState } from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"
import { RichText } from "prismic-reactjs"
import ReactSlider from "react-slider"
import SEO from "../components/SEO"
import Hero from "../components/hero"
import Trial from "../components/trial"
import linkResolver from "../utils/linkResolver"
import Image from "../components/image"
import { css } from "@emotion/core"
import { colors, mq } from "../styles/styles"
import PriceTable from "../components/priceTable"
import LocalizedLink from "../components/localizedLink"
import PricesPlanMobile from "../components/pricesPlanMobile"
import { isExternalLink, isDocumentLink } from "../utils/utils"

const PricePage = ({ data, pageContext: { locale }, location }) => {
  const pricePageData = data.prismic.pricepage
  const pricePageMobileData = data.prismic.pricepageMobile
  const [activeFaq, setActiveFaq] = useState(null)
  const [displayFullInfos, setDisplayFullInfos] = useState(null)
  const [isAnnualSubscription, setIsAnnualSubscription] = useState(true)
  const [numberOfUsers, setNumberOfUsers] = useState(1)
  const essential = 0
  const professional = 1
  const enterprise = 2

  const calcAnnual = (first, additional) => {
    return ((first + (numberOfUsers - 1) * additional) * 12).toFixed(2)
  }
  const calcMonthly = (first, additional) => {
    return (first + (numberOfUsers - 1) * additional).toFixed(2)
  }

  const calcAnnualByPlan = (subscriptionMode, plan) => {
    const prices =
      subscriptionMode === "annual"
        ? pricePageData.annual_subscription_list[plan]
        : pricePageData.monthly_subscription_list[plan]
    return calcAnnual(
      prices.price_for_one_user,
      prices.price_for_additional_user
    )
  }
  const calcMonthlyByPlan = plan => {
    const prices = pricePageData.monthly_subscription_list[plan]
    return calcMonthly(
      prices.price_for_one_user,
      prices.price_for_additional_user
    )
  }

  const yearEssential = calcAnnualByPlan(
    isAnnualSubscription ? "annual" : "monthly",
    essential
  )
  const yearProfessional = calcAnnualByPlan(
    isAnnualSubscription ? "annual" : "monthly",
    professional
  )
  const yearEnterprise = calcAnnualByPlan(
    isAnnualSubscription ? "annual" : "monthly",
    enterprise
  )

  const monthlyEssential = calcMonthlyByPlan(essential)
  const monthlyProfessional = calcMonthlyByPlan(professional)
  const monthlyEnterprise = calcMonthlyByPlan(enterprise)

  return (
    <>
      <SEO
        title={pricePageData.meta_title}
        desc={pricePageData.meta_description}
        metadata={data.site.siteMetadata}
        locale={locale}
        pathname={location.pathname}
      />
      <Hero
        hero={{
          title: pricePageData.title,
          subtitle: pricePageData.subtitle,
          image: {
            image: pricePageData.main_image,
            sharp: pricePageData.main_imageSharp,
          },
        }}
      />
      {pricePageData.richtext && (
        <div className="container">
          <div className="columns is-theme2">
            <div className="column is-6">
              RichText.render(pricePageData.richtext, linkResolver)
            </div>
          </div>
        </div>
      )}

      <section className="section is-medium" css={pricingCss}>
        <div className="container">
          <div className="plans">
            <PricesPlanMobile
              planNum={0}
              pricepageMobile={pricePageMobileData}
              priceYear={[yearEssential, yearProfessional, yearEnterprise]}
              priceMonth={[
                monthlyEssential,
                monthlyProfessional,
                monthlyEnterprise,
              ]}
            />
            <div className="columns plan__img is-hidden-touch">
              <div className="column is-3"></div>
              {pricePageData.plans.map((plan, i) => (
                <div key={i} className="column is-3">
                  <div className="box has-text-white has-text-centered">
                    {plan.plan_image && (
                      <figure className="image is-128x128">
                        <Image
                          sharp={plan.plan_imageSharp}
                          image={plan.plan_image}
                        />
                      </figure>
                    )}
                    <h3 className="title is-5 has-text-white has-text-centered is-uppercase has-text-weight-semibold">
                      {RichText.asText(plan.plan_name)}
                    </h3>
                    {plan.plan_description &&
                      RichText.render(plan.plan_description, linkResolver)}
                  </div>
                </div>
              ))}
            </div>
            {/* Number of users */}
            <div className="columns is-hidden-touch">
              <div className="column is-3">
                <h4 className="title is-6 has-text-centered">
                  {RichText.asText(pricePageData.number_of_users)}
                </h4>
              </div>
              <div className="column plan__user is-9">
                {/* <output for="sliderWithValue">50</output> */}
                {/* <input
                  className="slider is-fullwidth is-warning is-circle has-output-tooltip"
                  step="1"
                  min="0"
                  max="100"
                  id="sliderWithValue"
                  type="range"
                /> */}

                <div className="box">
                  <ReactSlider
                    css={reactSliderCss}
                    className="horizontal-slider"
                    thumbClassName="example-thumb"
                    trackClassName="example-track"
                    min={1}
                    max={25}
                    onChange={val => setNumberOfUsers(val)}
                    renderThumb={(props, state) => (
                      <div {...props} className="tag is-size-5 px-5">
                        {state.valueNow < 25
                          ? state.valueNow
                          : `${state.valueNow}+`}
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
            {/* Annual pre-payment subscription */}
            <div className="columns plan__pricing is-hidden-touch">
              <div className="column is-3 has-text-centered">
                <h4 className="title is-6">
                  {RichText.asText(pricePageData.annual_subscription)}
                </h4>
                <div className="field">
                  <input
                    id="annualSubscription"
                    type="checkbox"
                    name="annualSubscription"
                    className="switch"
                    checked={isAnnualSubscription}
                    onChange={() =>
                      setIsAnnualSubscription(!isAnnualSubscription)
                    }
                  />
                  <label htmlFor="annualSubscription"></label>
                </div>
              </div>
              {pricePageData.annual_subscription_list.map((price, i) => (
                <div key={i} className="column is-3">
                  <div
                    className={`box has-text-centered ${
                      isAnnualSubscription === true
                        ? "has-background-blue has-text-white"
                        : ""
                    }`}
                  >
                    {price.annual_subscription_list_price &&
                      RichText.render(
                        price.annual_subscription_list_price,
                        linkResolver
                      )}
                    <span
                      className={`tag ${
                        isAnnualSubscription === true
                          ? "has-background-white"
                          : ""
                      }
                    `}
                    >
                      {price.annual_subscription_list_price_aside &&
                        RichText.render(
                          price.annual_subscription_list_price_aside,
                          linkResolver
                        )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {/* Monthly subscription */}
            <div className="columns plan__pricing is-hidden-touch">
              <div className="column is-3 has-text-centered">
                <h4 className="title is-6">
                  {RichText.asText(pricePageData.monthly_subscription)}
                </h4>
                <div className="field">
                  <input
                    id="annualSubscription"
                    type="checkbox"
                    name="annualSubscription"
                    className="switch"
                    checked={!isAnnualSubscription}
                    onChange={() =>
                      setIsAnnualSubscription(isAnnualSubscription)
                    }
                  />
                  <label htmlFor="annualSubscription"></label>
                </div>
              </div>
              {pricePageData.monthly_subscription_list.map(price => (
                <div className="column is-3">
                  <div
                    className={`box has-text-centered ${
                      isAnnualSubscription === false
                        ? "has-background-blue has-text-white"
                        : ""
                    }`}
                  >
                    {price.monthly_subscription_list_price &&
                      RichText.render(
                        price.monthly_subscription_list_price,
                        linkResolver
                      )}
                    <span
                      className={`tag ${
                        isAnnualSubscription === false
                          ? "has-background-white"
                          : ""
                      }
                    `}
                    >
                      {price.monthly_subscription_list_price_aside &&
                        RichText.render(
                          price.monthly_subscription_list_price_aside,
                          linkResolver
                        )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {/* Annual total */}
            <div className="columns plan__pricing is-hidden-touch">
              <div className="column is-3">
                <h4 className="title is-6 has-text-centered">
                  {RichText.asText(pricePageData.annual_total)}
                </h4>
              </div>
              {/* calculated prices here */}
              {numberOfUsers < 25 && (
                <>
                  <div className="column is-3">
                    <div className="box has-text-centered">
                      {yearEssential} €/HT
                    </div>
                  </div>
                  <div className="column is-3">
                    <div className="box has-text-centered">
                      {yearProfessional} €/HT
                    </div>
                  </div>
                  <div className="column is-3">
                    <div className="box has-text-centered">
                      {yearEnterprise} €/HT
                    </div>
                  </div>
                </>
              )}
              {numberOfUsers === 25 && (
                <div className="column is-9">
                  <a
                    href={`mailto:contact@monstock.net?subject=Prices`}
                    className="button is-fullwidth"
                  >
                    {pricePageData.contact_us}
                  </a>
                </div>
              )}
            </div>
            {/* Monthly total */}
            {!isAnnualSubscription && (
              <div className="columns plan__pricing is-hidden-touch">
                <div className="column is-3">
                  <h4 className="title is-6 has-text-centered">
                    {RichText.asText(pricePageData.monthly_total)}
                  </h4>
                </div>
                {/* calculated prices here */}
                {numberOfUsers < 25 && (
                  <>
                    <div className="column is-3">
                      <div className="box has-text-centered">
                        {monthlyEssential} €/HT
                      </div>
                    </div>
                    <div className="column is-3">
                      <div className="box has-text-centered">
                        {monthlyProfessional} €/HT
                      </div>
                    </div>
                    <div className="column is-3">
                      <div className="box has-text-centered">
                        {monthlyEnterprise} €/HT
                      </div>
                    </div>
                  </>
                )}
                {numberOfUsers === 25 && (
                  <div className="column is-9">
                    <a
                      href={`mailto:contact@monstock.net?subject=Prices`}
                      className="button is-fullwidth"
                    >
                      {pricePageData.contact_us}
                    </a>
                  </div>
                )}
              </div>
            )}

            <div className="columns">
              <div className="column"></div>
              {pricePageData.trial.map((trial, i) => (
                <div key={i} className="column">
                  {trial.button_link && isExternalLink(trial.button_link) && (
                    <a href={trial.button_link.url}>{trial.button_text}</a>
                  )}
                  {trial.button_link && isDocumentLink(trial.button_link) && (
                    <LocalizedLink to={trial.button_link._meta.uid}>
                      {trial.button_text}
                    </LocalizedLink>
                  )}
                </div>
              ))}
            </div>
            <div className="columns my-4">
              <div className="column is-3 is-hidden-touch"></div>
              <div className="column has-text-centered">
                <button
                  onClick={() => setDisplayFullInfos(!displayFullInfos)}
                  className="button is-rounded has-background-white has-text-blue has-text-weight-medium button__gradient-border"
                >
                  {pricePageData.button_full_comparison}
                </button>
              </div>
            </div>
          </div>

          {displayFullInfos && (
            <>
              <div className="box box__full-comparison">
                <PriceTable
                  name={pricePageData.table_basic_title}
                  rows={pricePageData.body_basic}
                />
              </div>
              <div className="box box__full-comparison">
                <PriceTable
                  name={pricePageData.table_inventory_management_title}
                  rows={pricePageData.body_inventory_management}
                />
              </div>
              <div className="box box__full-comparison">
                <PriceTable
                  name={pricePageData.table_user_management_title}
                  rows={pricePageData.body_user_management}
                />
              </div>
              <div className="box box__full-comparison">
                <PriceTable
                  name={pricePageData.table_buying_and_selling_title}
                  rows={pricePageData.body_buying_and_selling}
                />
              </div>
              <div className="box box__full-comparison">
                <PriceTable
                  name={pricePageData.table_services_title}
                  rows={pricePageData.body_services}
                />
              </div>
              <div className="box box__full-comparison">
                <PriceTable
                  name={pricePageData.table_connectivity_and_integration_title}
                  rows={pricePageData.body_connectivity_and_integration}
                />
              </div>
              {pricePageData.body_delivery !== null &&
                pricePageData.body_delivery.length !== 0 && (
                  <div className="box box__full-comparison">
                    <PriceTable
                      name={pricePageData.table_delivery_title}
                      rows={pricePageData.body_delivery}
                    />
                  </div>
                )}
              {pricePageData.body_assistance !== null &&
                pricePageData.body_assistance.length !== 0 && (
                  <div className="box box__full-comparison">
                    <PriceTable
                      name={pricePageData.table_assistance_title}
                      rows={pricePageData.body_assistance}
                    />
                  </div>
                )}
              {pricePageData.body_data_hosting !== null &&
                pricePageData.body_data_hosting.length !== 0 && (
                  <div className="box box__full-comparison">
                    <PriceTable
                      name={pricePageData.table_data_hosting_title}
                      rows={pricePageData.body_data_hosting}
                    />
                  </div>
                )}
              {pricePageData.body_other !== null &&
                pricePageData.body_other.length !== 0 && (
                  <div className="box box__full-comparison">
                    <PriceTable
                      name={pricePageData.table_other_title}
                      rows={pricePageData.body_other}
                    />
                  </div>
                )}

              <div className="columns">
                <div className="column"></div>
                {pricePageData.trial.map(trial => (
                  <div
                    key={trial.button_id}
                    className="column has-text-centered"
                  >
                    {trial.button_link && isExternalLink(trial.button_link) && (
                      <a href={trial.button_link.url}>{trial.button_text}</a>
                    )}
                    {trial.button_link && isDocumentLink(trial.button_link) && (
                      <LocalizedLink to={trial.button_link._meta.uid}>
                        {trial.button_text}
                      </LocalizedLink>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="section is-theme2" css={proServicesCss}>
        <div className="container has-text-centered pb-6">
          <h3 className="title is-3 has-text-weight-semibold py-6">
            {RichText.asText(pricePageData.optional_title)}
          </h3>
          <div className="columns is-centered is-multiline has-background-white">
            {pricePageData.optional.map((option, i) => (
              <div key={i} className="column is-half py-6">
                <h5 className="title is-5 has-text-weight-semibold">
                  {RichText.asText(option.optional_list_title)}
                </h5>
                {option.optional_list_description &&
                  RichText.render(
                    option.optional_list_description,
                    linkResolver
                  )}
              </div>
            ))}
            <div className="column is-full is-theme1 py-6">
              <h4 className="title is-4 has-text-weight-semibold">
                {RichText.asText(pricePageData.optional_footer_title)}
              </h4>
              <LocalizedLink
                to={pricePageData.optional_button_link._meta.uid}
                className="button is-rounded button__primary has-text-white"
              >
                {pricePageData.optional_button_text}
              </LocalizedLink>
            </div>
          </div>
        </div>
      </section>

      <section className="section" css={faqCss}>
        <div className="container has-text-centered">
          <a
            href={pricePageData.faq_button_link.url}
            className="button is-rounded is-outlined is-link"
          >
            {pricePageData.faq_button_text}
          </a>
        </div>
      </section>

      <Trial trial={data.prismic.allTrialcomponents.edges[0].node} />
    </>
  )
}

export default PricePage

PricePage.propTypes = {
  data: PropTypes.shape({
    prismic: PropTypes.object.isRequired,
  }).isRequired,
}

export const PriceQuery = graphql`
  query PriceQuery($uid: String!, $locale: String!) {
    prismic {
      pricepageMobile: pricepage(uid: $uid, lang: $locale) {
        meta_title
        meta_description
        title
        subtitle
        richtext
        main_image
        main_imageSharp {
          extension
          childImageSharp {
            fluid {
              ...GatsbyImageSharpFluid_tracedSVG
            }
          }
        }
        plans {
          plan_image
          plan_name
          plan_description
        }

        number_of_users
        annual_subscription
        annual_subscription_list {
          annual_subscription_list_price
          annual_subscription_list_price_aside
          price_for_one_user
          price_for_additional_user
        }
        monthly_subscription
        monthly_subscription_list {
          monthly_subscription_list_price
          monthly_subscription_list_price_aside
          price_for_one_user
          price_for_additional_user
        }
        annual_total
        monthly_total
        button_full_comparison
      }

      pricepage(uid: $uid, lang: $locale) {
        meta_title
        meta_description
        title
        subtitle
        richtext
        main_image
        main_imageSharp {
          extension
          childImageSharp {
            fluid {
              ...GatsbyImageSharpFluid_tracedSVG
            }
          }
        }
        plans {
          plan_image
          plan_name
          plan_description
        }

        number_of_users
        annual_subscription
        annual_subscription_list {
          annual_subscription_list_price
          annual_subscription_list_price_aside
          price_for_one_user
          price_for_additional_user
        }
        monthly_subscription
        monthly_subscription_list {
          monthly_subscription_list_price
          monthly_subscription_list_price_aside
          price_for_one_user
          price_for_additional_user
        }
        annual_total
        monthly_total
        button_full_comparison

        table_basic_title
        body_basic {
          ... on PRISMIC_PricepageBody_basicRow {
            primary {
              row_name
            }
            fields {
              row_value_bool
              row_value_text
            }
          }
        }

        table_inventory_management_title
        body_inventory_management {
          ... on PRISMIC_PricepageBody_inventory_managementRow {
            primary {
              row_name
            }
            fields {
              row_value_bool
              row_value_text
            }
          }
        }
        table_user_management_title
        body_user_management {
          ... on PRISMIC_PricepageBody_user_managementRow {
            primary {
              row_name
            }
            fields {
              row_value_bool
              row_value_text
            }
          }
        }
        table_buying_and_selling_title
        body_buying_and_selling {
          ... on PRISMIC_PricepageBody_buying_and_sellingRow {
            primary {
              row_name
            }
            fields {
              row_value_bool
              row_value_text
            }
          }
        }
        table_services_title
        body_services {
          ... on PRISMIC_PricepageBody_servicesRow {
            primary {
              row_name
            }
            fields {
              row_value_bool
              row_value_text
            }
          }
        }
        table_connectivity_and_integration_title
        body_connectivity_and_integration {
          ... on PRISMIC_PricepageBody_connectivity_and_integrationRow {
            primary {
              row_name
            }
            fields {
              row_value_bool
              row_value_text
            }
          }
        }
        table_delivery_title
        body_delivery {
          ... on PRISMIC_PricepageBody_deliveryRow {
            primary {
              row_name
            }
            fields {
              row_value_bool
              row_value_text
            }
          }
        }
        table_assistance_title
        body_assistance {
          ... on PRISMIC_PricepageBody_assistanceRow {
            primary {
              row_name
            }
            fields {
              row_value_bool
              row_value_text
            }
          }
        }
        table_data_hosting_title
        body_data_hosting {
          ... on PRISMIC_PricepageBody_data_hostingRow {
            primary {
              row_name
            }
            fields {
              row_value_bool
              row_value_text
            }
          }
        }
        table_other_title
        body_other {
          ... on PRISMIC_PricepageBody_otherRow {
            primary {
              row_name
            }
            fields {
              row_value_bool
              row_value_text
            }
          }
        }
        contact_us
        optional_title
        optional_button_text
        optional_footer_title
        optional_button_link {
          ... on PRISMIC__Document {
            _meta {
              uid
              lang
            }
          }
        }
        optional {
          optional_list_title
          optional_list_description
        }
        trial {
          button_id
          button_text
          button_link {
            ... on PRISMIC__Document {
              _meta {
                uid
                lang
              }
            }
          }
        }
        faq_button_text
        faq_button_link {
          ... on PRISMIC__ExternalLink {
            url
          }
        }
      }
      ...Trial
    }

    site {
      ...SiteInformation
    }
  }
`

const pricingCss = css`
  .switch[type="checkbox"] + label::before,
  .switch[type="checkbox"] + label:before {
    border-radius: 50px;
  }

  .switch[type="checkbox"] + label::after,
  .switch[type="checkbox"] + label:after {
    border-radius: 50%;
  }

  .switch[type="checkbox"]:checked + label::before,
  .switch[type="checkbox"]:checked + label:before {
    background-image: ${colors.orangeGradient};
    background-color: #fff;
    border: 1px solid transparent;
  }

  .switch[type="checkbox"]:checked + label::after,
  .switch[type="checkbox"]:checked + label:after {
    background-color: #fff;
  }

  .switch[type="checkbox"] + label::before,
  .switch[type="checkbox"] + label:before {
    background-color: #fff;
    border: 1px solid ${colors.blue};
  }

  .switch[type="checkbox"] + label::after,
  .switch[type="checkbox"] + label:after {
    background-color: ${colors.blue};
  }

  .plan__img {
    .column {
      display: flex;
    }
    .column:nth-child(even) .box {
      background-color: ${colors.blue};
    }
    .column:nth-child(odd) .box {
      background-image: ${colors.orangeGradient};
    }
    .box {
      position: relative;
      padding: 80px 30px 40px;
    }
    .image {
      position: absolute;
      top: -125px;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  .plan__pricing,
  .plan__user {
    .box {
      border: 1px solid ${colors.lightgray};
      box-shadow: none;
    }
  }

  .plan__pricing {
    .column:first-of-type {
      border-top: 1px dashed ${colors.borderForm};
      display: flex;
      flex-direction: column;
      justify-content: center;
    }

    .tag {
      background-color: ${colors.blueLightOne};
      width: 100%;
      padding: 20px;
      margin-top: 20px;
    }
  }

  .plans {
    .plan__pricing .column:nth-of-type(3) {
      border: 4px solid ${colors.orange};
      border-top: none;
      border-bottom: none;
    }

    .columns:nth-of-type(6) .column:nth-of-type(3) {
      border-top: 4px solid ${colors.orange};
      border-top-left-radius: 9px;
      border-top-right-radius: 9px;
    }

    .columns:nth-last-child(3) .column:nth-of-type(3) {
      border-bottom: 4px solid ${colors.orange};
      border-bottom-left-radius: 9px;
      border-bottom-right-radius: 9px;
    }
  }

  .box__full-comparison {
    box-shadow: none;
  }

  .plans__mobile {
    &:nth-of-type(odd)
      .plans__mobile-type.is-hidden-desktop
      .plan__mobile-header
      .box {
      background-color: ${colors.blue};
    }
    &:nth-of-type(even)
      .plans__mobile-type.is-hidden-desktop
      .plan__mobile-header
      .box {
      background-image: ${colors.orangeGradient};
    }
  }
`

const proServicesCss = css`
  .columns:first-of-type {
    border: 1px solid ${colors.lightgray};
    -webkit-box-shadow: 0px 2px 4px 0px ${colors.lightgray};
    -moz-box-shadow: 0px 2px 4px 0px ${colors.lightgray};
    box-shadow: 0px 2px 4px 0px ${colors.lightgray};
  }

  .column:nth-child(even) {
    border-width: 0px 0px 1px 0px;
    border-color: ${colors.lightgray};
    border-style: solid;
  }

  .column:nth-child(odd) {
    border-width: 0px 1px 1px 0px;
    border-color: ${colors.lightgray};
    border-style: solid;
  }

  .column:last-of-type {
    border: 0;
  }

  @media (max-width: 768px) {
    .column:nth-child(odd) {
      border-width: 0px 0px 1px 0px;
    }
  }
`

const faqCss = css``

const reactSliderCss = css`
  &.horizontal-slider {
    background: lightgrey;
    padding: 10px;
    border-radius: 10px;

    .tag {
      position: relative;
      background: #94a2cd;
      border-radius: 0.4em;
    }

    .tag:after {
      content: "";
      position: absolute;
      top: 0;
      left: 50%;
      width: 0;
      height: 0;
      border: 5px solid transparent;
      border-bottom-color: #94a2cd;
      border-top: 0;
      margin-left: -5px;
      margin-top: -5px;
    }
  }
`
